import { css } from '@emotion/react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Dispatch, FC, useEffect, useState } from 'react';

import { PaymentPeriod } from '@features/payments';
import { successGreen, borderGrey, white } from '@styles/colors';
import { bold, light } from '@styles/fontWeights';

import { Plan, pricingOptions } from './constants';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentInformationForm from '@features/employerOnboarding/components/PaymentInformationForm/PaymentInformationForm';

type PricingOptionsProps = {
  useLightButtons?: boolean;
  showDiscounts?: boolean;
  selectedPlan?: Plan | null;
  handleSelectPlan?: (plan: Plan) => void;
  setPaymentPeriod?: Dispatch<PaymentPeriod>;
  initialPaymentPeriod?: PaymentPeriod;
};
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);
const PricingOptions: FC<PricingOptionsProps> = ({
  useLightButtons = false,
  showDiscounts = false,
  selectedPlan,
  handleSelectPlan = null,
  setPaymentPeriod = () => {},
  initialPaymentPeriod = 'Annually',
}) => {
  const theme = useTheme();
  const isSmOrHigher = useMediaQuery(theme.breakpoints.up('sm'));

  const [subscriptionPeriod, setSubscriptionPeriod] =
    useState<PaymentPeriod>(initialPaymentPeriod);
  const [pricingDescription, setPricingDescription] = useState<
    | '(billed monthly)'
    | '(13% discount <br/> billed biannually)'
    | '(25% discount <br/> billed annually)'
  >('(25% discount <br/> billed annually)');

  useEffect(() => {
    switch (subscriptionPeriod) {
      case 'Monthly':
        setPricingDescription('(billed monthly)');
        setPaymentPeriod('Monthly');
        break;
      case 'Semi-Annually':
        setPricingDescription('(13% discount <br/> billed biannually)');
        setPaymentPeriod('Semi-Annually');
        break;
      case 'Annually':
        setPricingDescription('(25% discount <br/> billed annually)');
        setPaymentPeriod('Annually');
        break;
    }
  }, [subscriptionPeriod]);

  return (
    <Box
      component="section"
      paddingBottom={9}
      sx={{
        mt: -19,
      }}
    >
      <Container>
        <Box
          display="block"
          textAlign="center"
          marginBottom={5}
          css={css`
            button {
              ${useLightButtons &&
              `
              color: ${white};
              border-color: ${white};
              
              &:hover {
                border-color: ${white};
              }
              `}
              ${theme.breakpoints.up('sm')} {
                width: 110px;
                display: inline-block;
              }
            }
          `}
        >
          <Stack
            direction={isSmOrHigher ? 'row' : 'column'}
            alignSelf="center"
            textAlign="center"
            justifyContent="center"
          >
            <Box width="100%">
              <Button
                sx={{ width: '150px !important', padding: '10px 0' }}
                onClick={() => setSubscriptionPeriod('Monthly')}
                variant={
                  subscriptionPeriod === 'Monthly' ? 'contained' : 'outlined'
                }
              >
                Monthly
              </Button>
              {showDiscounts && (
                <Typography variant="h6" mt={2}>
                  First 7 Days are on us!
                </Typography>
              )}
            </Box>
            <Box width="100%">
              <Button
                sx={{ width: '150px !important', padding: '10px 0' }}
                onClick={() => setSubscriptionPeriod('Semi-Annually')}
                variant={
                  subscriptionPeriod === 'Semi-Annually'
                    ? 'contained'
                    : 'outlined'
                }
              >
                Semi-Annual
              </Button>
             {showDiscounts && (  <Typography
                variant="h5"
                mt={2}
                color={successGreen}
                fontWeight={bold}
              >
                13% discount
              </Typography>)}
            </Box>
            <Box width="100%">
              <Button
                sx={{ width: '150px !important', padding: '10px 0' }}
                onClick={() => setSubscriptionPeriod('Annually')}
                variant={
                  subscriptionPeriod === 'Annually' ? 'contained' : 'outlined'
                }
              >
                Annual
              </Button>
            {showDiscounts && (   <Typography
                variant="h5"
                mt={2}
                color={successGreen}
                fontWeight={bold}
              >
                25% discount
              </Typography>)}
            </Box>
          </Stack>
        </Box>
        <Grid
          sx={{ borderRadius: '8px' }}
          spacing={3}
          gap={2}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {pricingOptions.slice(0, -1).map((option, index) => {
            const isEnterprise = option.title === 'StaffedUp Enterprise';

            const previousOptions = [];

            while (pricingOptions[index - 1]) {
              previousOptions.push(...pricingOptions[index - 1].benefits);
              index--;
            }

            return (
              <Grid
                sx={{
                  borderRadius: 8,
                  border:
                    selectedPlan === option.title ? '5px solid black' : '',
                }}
                item
                xs={12}
                key={option.title}
                // md={4}
              >
                <Card
                  sx={{
                    // maxWidth: '300px',
                    height: '100%',
                    margin: '0 auto',
                    cursor: handleSelectPlan ? 'pointer' : 'initial',
                  }}
                  onClick={() => {
                    if (handleSelectPlan) {
                      handleSelectPlan(option.title);
                    }
                  }}
                >
                  <Stack direction={isSmOrHigher ? 'row' : 'column'}>
                    <Box>
                      <CardHeader
                        title={option.title}
                        sx={{
                          backgroundColor: 'secondary.main',
                          fontWeight: bold,
                        }}
                        titleTypographyProps={{
                          variant: 'h4',
                          color: white,
                          fontWeight: bold,
                          textAlign: 'center',
                          width: '274px',
                        }}
                      />
                      <Box
                        bgcolor="primary.main"
                        color={white}
                        textAlign="center"
                        padding={2}
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                      >
                        <Typography
                          textAlign="center"
                          color={white}
                          fontWeight={light}
                          lineHeight="1.1"
                          sx={{
                            fontSize: {
                              xs: '23px',
                              md: '27px',
                            },
                          }}
                        >
                          {!isEnterprise && option.pricePerMonth
                            ? option.pricePerMonth[subscriptionPeriod]!
                            : 'ENTERPRISE DISCOUNTS AVAILABLE'}
                        </Typography>
                        {!isEnterprise && (
                          <Typography
                            textAlign="center"
                            color={white}
                            fontWeight={light}
                            fontSize="18px"
                            component="div"
                            dangerouslySetInnerHTML={{
                              __html: pricingDescription,
                            }}
                          />
                        )}
                        <Divider
                          sx={{ width: '100%', margin: 2, background: white }}
                        />
                        <Typography
                          textAlign="center"
                          color={white}
                          fontWeight={light}
                          lineHeight="1.1"
                        >
                          {option.acctualBill
                            ? option.acctualBill[subscriptionPeriod]!
                            : 'ENTERPRISE DISCOUNTS AVAILABLE'}
                        </Typography>
                      </Box>
                    </Box>
                    <CardContent
                      sx={{
                        flex: '1 0 auto',
                      }}
                    >
                      {!isSmOrHigher && previousOptions.length ? (
                        <Box
                          bgcolor={successGreen}
                          marginTop={2}
                          padding={2}
                          display="flex"
                          justifyContent="center"
                        >
                          <Typography
                            color={white}
                            display="flex"
                            alignItems="center"
                            textAlign="center"
                          >
                            <CheckCircleIcon
                              color="inherit"
                              sx={{ marginRight: 3 }}
                            />
                            All of the above, plus:
                          </Typography>
                        </Box>
                      ) : null}
                      <List
                        css={css`
                          li {
                            justify-content: center;
                            min-height: 40px;

                            border-bottom: 1px solid ${borderGrey};
                          }
                        `}
                      >
                        <Stack direction={isSmOrHigher ? 'row' : 'column'}>
                          {isSmOrHigher &&
                            option.title === 'Service + Onboarding' && (
                              <Stack minWidth="190px">
                                {previousOptions.slice(0, 4).map((opt) => (
                                  <ListItem key={opt}>
                                    <CheckCircleIcon
                                      color="success"
                                      fontSize="small"
                                      sx={{ mt: '12px' }}
                                    />
                                  </ListItem>
                                ))}
                              </Stack>
                            )}
                          <Box>
                            {option.benefits.slice(0, 5).map((opt, index) => (
                              <ListItem key={index}>
                                <Typography variant="subtitle1">
                                  {opt}
                                </Typography>
                              </ListItem>
                            ))}
                          </Box>
                          <Box>
                            {option.benefits.slice(5, 10).map((opt, index) => (
                              <ListItem key={index + 5}>
                                <Typography variant="subtitle1">
                                  {opt}
                                </Typography>
                              </ListItem>
                            ))}
                          </Box>
                        </Stack>
                      </List>
                    </CardContent>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default PricingOptions;
